import type { PackBuilderItem, PaginationMeta } from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Pagination } from '../../../components/ui/Pagination';
import { PackItemRow } from './PackItemRow';

interface Props {
  items: PackBuilderItem[];
  loading: boolean;
  error: string | null;
  meta: PaginationMeta | null;
  page: number;
  onPageChange: (page: number) => void;
  /** Ids ticked on this page. The tab clears it whenever the result set changes. */
  selected: number[];
  onSelectedChange: (ids: number[]) => void;
  currency: string | null;
  busy: boolean;
  /** limits.max_items_per_request — a bigger selection is cut to it. */
  maxPerRequest: number;
  /** Support-disabled: the write answers 409, so Remove is not offered. */
  readOnly?: boolean;
  readOnlyHint?: string;
  emptyLabel?: string;
  onRemove: (ids: number[]) => void;
}

const COLUMNS = ['Card', 'Grade', 'Value', 'Bucket', 'Status'];

const headCell =
  'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';

/**
 * What the pack actually holds, as Postgres knows it. Every member is here
 * whatever its status, so the only verb is Remove — adding is a separate mode
 * over the searchable inventory.
 */
export function PackContentsGrid({
  items,
  loading,
  error,
  meta,
  page,
  onPageChange,
  selected,
  onSelectedChange,
  currency,
  busy,
  maxPerRequest,
  readOnly = false,
  readOnlyHint,
  emptyLabel = 'This pack has no cards yet.',
  onRemove,
}: Props) {
  const pageIds = items.map((item) => item.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someSelected = pageIds.some((id) => selected.includes(id));
  const toRemove = items.filter((item) => selected.includes(item.id)).map((item) => item.id);
  const totalPages = meta ? Math.ceil(meta.total_count / meta.per) : 0;

  function toggleOne(item: PackBuilderItem, checked: boolean) {
    onSelectedChange(checked ? [...selected, item.id] : selected.filter((id) => id !== item.id));
  }

  function toggleAll(checked: boolean) {
    onSelectedChange(
      checked
        ? [...new Set([...selected, ...pageIds])]
        : selected.filter((id) => !pageIds.includes(id)),
    );
  }

  return (
    <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--co-border)] px-4 py-2">
        <span className="text-[11px] text-[var(--co-muted)]">
          {readOnly && readOnlyHint ? (
            <span className="italic">{readOnlyHint}</span>
          ) : (
            <>
              {selected.length} selected
              {selected.length > maxPerRequest ? ` — at most ${maxPerRequest} per action` : ''}
            </>
          )}
        </span>
        <Button
          variant="ghost"
          onClick={() => onRemove(toRemove.slice(0, maxPerRequest))}
          disabled={readOnly || busy || toRemove.length === 0}
          className="px-3 py-1 text-[12px]"
        >
          Remove selected ({toRemove.length})
        </Button>
      </div>

      {error ? (
        <p className="px-4 py-6 text-[13px] text-[var(--co-error)]">Error: {error}</p>
      ) : loading && items.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-[var(--co-muted)]">Loading the pack…</p>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-[var(--co-muted)]">{emptyLabel}</p>
      ) : (
        <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
          <table aria-label="Pack contents" className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--co-border)]">
                <th className={headCell} scope="col">
                  <Checkbox
                    aria-label="Select every card on this page"
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={toggleAll}
                  />
                </th>
                {COLUMNS.map((column) => (
                  <th key={column} scope="col" className={headCell}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <PackItemRow
                  key={item.id}
                  item={item}
                  selected={selected.includes(item.id)}
                  currency={currency}
                  onSelectChange={toggleOne}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={meta.total_count}
          noun="cards"
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
